import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Loader2 } from "lucide-react";
import api from "@/lib/api";

export const AdminVouchers = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ code: "", discount_type: "percent", value: "", expires_at: "", max_redemptions: "", once_per_customer: true, active: true });

    const load = () => {
        setLoading(true);
        api.get("/admin/vouchers").then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.code.trim() || !form.value) { toast.error("Informe código e valor."); return; }
        setSaving(true);
        try {
            await api.post("/admin/vouchers", {
                code: form.code.trim().toUpperCase(),
                discount_type: form.discount_type,
                value: parseFloat(form.value),
                expires_at: form.expires_at || null,
                max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
                once_per_customer: form.once_per_customer,
                active: form.active,
            });
            toast.success("Cupom criado.");
            setForm({ code: "", discount_type: "percent", value: "", expires_at: "", max_redemptions: "", once_per_customer: true, active: true });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar cupom."); }
        finally { setSaving(false); }
    };

    const toggle = async (v) => {
        try {
            await api.patch(`/admin/vouchers/${v.id}`, { active: !v.active });
            setRows((rs) => rs.map((x) => (x.id === v.id ? { ...x, active: !x.active } : x)));
        } catch (e) { toast.error("Não foi possível alterar o cupom."); }
    };

    return (
        <div className="space-y-6" data-testid="admin-vouchers">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
                <h3 className="font-heading font-bold text-sm text-petroleum flex items-center gap-1.5"><Ticket className="w-4 h-4 text-mineral" /> Criar cupom de desconto</h3>
                <p className="text-xs text-slate-500 mt-0.5">Válido para assinaturas. O desconto (em % ou R$) é aplicado por 12 meses ao assinante que digitar o código na compra.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 mt-4">
                    <div className="lg:col-span-2">
                        <label className="text-xs text-slate-500">Código</label>
                        <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="EX: LANCAMENTO20" data-testid="voucher-code" className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded uppercase focus:outline-none focus:border-mineral" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">Tipo</label>
                        <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="voucher-type" className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-mineral">
                            <option value="percent">% percentual</option>
                            <option value="amount">R$ valor fixo</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">{form.discount_type === "percent" ? "Desconto (%)" : "Desconto (R$)"}</label>
                        <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.discount_type === "percent" ? "20" : "100"} data-testid="voucher-value" className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-mineral" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">Expira em (opcional)</label>
                        <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="voucher-expires" className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-mineral" />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500">Limite de usos (opcional)</label>
                        <input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="ilimitado" data-testid="voucher-max" className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-mineral" />
                    </div>
                </div>
                <label className="flex items-center gap-2 mt-3 text-xs text-slate-600 cursor-pointer" data-testid="voucher-once-label">
                    <input type="checkbox" checked={form.once_per_customer} onChange={(e) => setForm({ ...form, once_per_customer: e.target.checked })} data-testid="voucher-once" className="accent-mineral w-4 h-4" />
                    Limitar a <b>1 uso por cliente</b> (evita que a mesma conta use o cupom mais de uma vez)
                </label>
                <button onClick={create} disabled={saving} data-testid="voucher-create" className="mt-4 inline-flex items-center gap-2 bg-mineral hover:bg-mineralHover text-white rounded-sm px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Criar cupom
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
                {loading ? <div className="p-8 text-center text-sm text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">Nenhum cupom criado ainda.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b-2 border-slate-200 text-left text-xs uppercase text-slate-500">
                            <th className="py-3 px-4">Código</th><th className="py-3 px-4">Desconto</th><th className="py-3 px-4">Assinaturas geradas</th><th className="py-3 px-4">Expira</th><th className="py-3 px-4">Status</th></tr></thead>
                        <tbody>{rows.map((v) => (
                            <tr key={v.id} className="border-b border-slate-100" data-testid={`voucher-row-${v.code}`}>
                                <td className="py-3 px-4 font-mono font-semibold">{v.code}{v.once_per_customer && <span className="ml-2 text-[10px] font-bold text-petroleum/70">1/cliente</span>}</td>
                                <td className="py-3 px-4">{v.discount_type === "percent" ? `${v.value}%` : `R$ ${Number(v.value).toLocaleString("pt-BR")}`} <span className="text-slate-400">· 12 meses</span></td>
                                <td className="py-3 px-4 font-semibold" data-testid={`voucher-count-${v.code}`}>{v.redemptions || 0}{v.max_redemptions ? ` / ${v.max_redemptions}` : ""}</td>
                                <td className="py-3 px-4">{v.expires_at ? new Date(v.expires_at).toLocaleDateString("pt-BR") : "—"}</td>
                                <td className="py-3 px-4">
                                    <button onClick={() => toggle(v)} data-testid={`voucher-toggle-${v.code}`} className={`text-xs font-bold px-2.5 py-1 rounded-full ${v.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                                        {v.active ? "Ativo" : "Inativo"}
                                    </button>
                                </td>
                            </tr>
                        ))}</tbody>
                    </table>
                )}
            </div>
        </div>
    );
};
